import { useState, useEffect } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { ShieldCheck, ArrowUpRight, Loader2, Radio } from 'lucide-react';
import { toast } from 'sonner';
import { VALUATION } from '../constants/testIds';
import { formatApiError } from '../contexts/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const SHIELD_IMG = '/sovereign-shield.png';

const defaultForm = {
  property_type: 'Penthouse',
  location: 'Ikoyi, Lagos',
  country: 'Nigeria',
  bedrooms: 4,
  bathrooms: 5,
  area_sqft: 6500,
  condition: 'Excellent',
  features: '',
  notes: '',
};

export default function Valuation() {
  const [form, setForm] = useState(defaultForm);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [signal, setSignal] = useState(null);

  useEffect(() => {
    axios.get(`${API}/valuation/signal-status`)
      .then(({ data }) => setSignal(data))
      .catch(() => setSignal(null));
  }, []);

  const currentSignal = signal && form.country
    ? (form.country === 'USA' ? signal.usa : signal.nigeria)
    : null;

  const onChange = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setResult(null);
    try {
      const payload = {
        ...form,
        bedrooms: Number(form.bedrooms),
        bathrooms: Number(form.bathrooms),
        area_sqft: Number(form.area_sqft),
        features: form.features.split(',').map((s) => s.trim()).filter(Boolean),
      };
      const { data } = await axios.post(`${API}/valuation`, payload);
      setResult(data);
      toast.success('Valuation issued under sovereign seal.');
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || 'The shield is reloading. Retry shortly.');
    } finally {
      setLoading(false);
    }
  };

  const reset = () => { setForm(defaultForm); setResult(null); };

  return (
    <div className="px-6 md:px-12 lg:px-24 py-16">
      <div className="grid grid-cols-1 md:grid-cols-12 gap-12 mb-16">
        <div className="md:col-span-7">
          <div className="flex items-center gap-3 mb-6 flex-wrap">
            <div className="text-[10px] tracking-[0.4em] uppercase text-[#D4AF37]">AI Valuation Shield</div>
            {currentSignal && (
              <span
                data-testid="valuation-signal-pill"
                className={`text-[9px] tracking-[0.3em] uppercase px-3 py-1.5 border inline-flex items-center gap-2 ${
                  currentSignal.status === 'LIVE'
                    ? 'border-[#D4AF37] text-[#D4AF37] bg-[#D4AF37]/10'
                    : 'border-[#444] text-[#A3A3A3]'
                }`}
              >
                <Radio size={10} strokeWidth={1.5} className={currentSignal.status === 'LIVE' ? 'text-[#D4AF37]' : 'text-[#A3A3A3]'} />
                {currentSignal.status === 'LIVE' ? '● LIVE SIGNAL' : '○ FOUNDRY SIGNAL'}
                <span className="text-[#666] normal-case tracking-normal hidden md:inline">·</span>
                <span className="text-[#A3A3A3] normal-case tracking-normal text-[10px] hidden md:inline">{currentSignal.label}</span>
              </span>
            )}
          </div>
          <h1 className="text-5xl md:text-6xl lg:text-7xl font-extralight leading-[1] tracking-tight">
            A sovereign-grade<br />
            <span className="font-display italic text-[#D4AF37]">appraisal in seconds.</span>
          </h1>
        </div>
        <div className="md:col-span-5 flex items-end">
          <p className="text-base text-[#A3A3A3] leading-relaxed font-light">
            Feed the Shield a property profile from either corridor.
            It returns valuation, confidence band, corridor relevance, risk flags, and a recommended posture.
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-12 gap-8 md:gap-16">
        <form onSubmit={submit} className="md:col-span-7 space-y-10">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div>
              <label className="text-[10px] tracking-[0.3em] uppercase text-[#A3A3A3] block mb-2">Property Type</label>
              <input data-testid={VALUATION.propertyType} className="input-sovereign"
                value={form.property_type} onChange={(e) => onChange('property_type', e.target.value)} required />
            </div>
            <div>
              <label className="text-[10px] tracking-[0.3em] uppercase text-[#A3A3A3] block mb-2">Country</label>
              <select data-testid={VALUATION.country} className="input-sovereign cursor-pointer"
                value={form.country} onChange={(e) => onChange('country', e.target.value)}>
                <option value="Nigeria">Nigeria</option>
                <option value="USA">USA / Hawaii</option>
              </select>
            </div>
            <div className="md:col-span-2">
              <label className="text-[10px] tracking-[0.3em] uppercase text-[#A3A3A3] block mb-2">Location</label>
              <input data-testid={VALUATION.location} className="input-sovereign"
                value={form.location} onChange={(e) => onChange('location', e.target.value)} required />
            </div>
            <div>
              <label className="text-[10px] tracking-[0.3em] uppercase text-[#A3A3A3] block mb-2">Bedrooms</label>
              <input data-testid={VALUATION.bedrooms} type="number" min="1" className="input-sovereign"
                value={form.bedrooms} onChange={(e) => onChange('bedrooms', e.target.value)} required />
            </div>
            <div>
              <label className="text-[10px] tracking-[0.3em] uppercase text-[#A3A3A3] block mb-2">Bathrooms</label>
              <input data-testid={VALUATION.bathrooms} type="number" min="1" className="input-sovereign"
                value={form.bathrooms} onChange={(e) => onChange('bathrooms', e.target.value)} required />
            </div>
            <div>
              <label className="text-[10px] tracking-[0.3em] uppercase text-[#A3A3A3] block mb-2">Area (sqft)</label>
              <input data-testid={VALUATION.area} type="number" min="500" className="input-sovereign"
                value={form.area_sqft} onChange={(e) => onChange('area_sqft', e.target.value)} required />
            </div>
            <div>
              <label className="text-[10px] tracking-[0.3em] uppercase text-[#A3A3A3] block mb-2">Condition</label>
              <select data-testid={VALUATION.condition} className="input-sovereign cursor-pointer"
                value={form.condition} onChange={(e) => onChange('condition', e.target.value)}>
                <option>Pristine</option>
                <option>Excellent</option>
                <option>Good</option>
                <option>Renovation Required</option>
              </select>
            </div>
            <div className="md:col-span-2">
              <label className="text-[10px] tracking-[0.3em] uppercase text-[#A3A3A3] block mb-2">Features (comma-separated)</label>
              <input className="input-sovereign"
                placeholder="OCEANFRONT, HELIPAD, SMART HOME, PRIVATE MARINA"
                value={form.features} onChange={(e) => onChange('features', e.target.value)} />
            </div>
            <div className="md:col-span-2">
              <label className="text-[10px] tracking-[0.3em] uppercase text-[#A3A3A3] block mb-2">Notes</label>
              <textarea data-testid={VALUATION.notes} className="input-sovereign resize-none" rows={3}
                value={form.notes} onChange={(e) => onChange('notes', e.target.value)} />
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-6 pt-4">
            <button type="submit" data-testid={VALUATION.submit} disabled={loading} className="btn-sovereign">
              {loading ? (
                <><Loader2 size={16} className="animate-spin" /> <span>Computing</span></>
              ) : (
                <><span>Run The Shield</span> <ArrowUpRight size={16} strokeWidth={1.5} /></>
              )}
            </button>
            <button type="button" onClick={reset} data-testid={VALUATION.resetBtn}
              className="text-[11px] tracking-[0.3em] uppercase text-[#A3A3A3] hover:text-[#D4AF37] transition-colors">
              Reset
            </button>
          </div>
        </form>

        <aside className="md:col-span-5">
          <div className="sticky top-28 sov-border p-8 md:p-10 bg-[#0A0A0A]">
            {!result ? (
              <div>
                <img src={SHIELD_IMG} alt="Shield" className="w-full mb-6 opacity-90" />
                <div className="text-[10px] tracking-[0.4em] uppercase text-[#D4AF37] mb-3">The Shield Awaits</div>
                <p className="text-sm text-[#A3A3A3] font-light leading-relaxed">
                  Submit a property profile. The Shield will return a sovereign-grade valuation
                  with full corridor analysis.
                </p>
                <div className="mt-8 flex items-center gap-3 text-[10px] tracking-[0.3em] uppercase text-[#A3A3A3]">
                  <ShieldCheck size={14} className="text-[#D4AF37]" strokeWidth={1} />
                  Powered by GPT · Bridge-Aware
                </div>
              </div>
            ) : (
              <div data-testid={VALUATION.resultBlock}>
                {result.signal_label && (
                  <div className={`text-[9px] tracking-[0.3em] uppercase mb-4 px-3 py-2 border inline-flex items-center gap-2 ${
                    result.signal_status === 'LIVE' ? 'border-[#D4AF37] text-[#D4AF37] bg-[#D4AF37]/10' : 'border-[#444] text-[#A3A3A3]'
                  }`}>
                    <Radio size={10} strokeWidth={1.5} /> {result.signal_label}
                  </div>
                )}
                <div className="text-[10px] tracking-[0.4em] uppercase text-[#D4AF37] mb-3">Estimated Value</div>
                <div className="font-display text-5xl md:text-6xl text-[#D4AF37] mb-4">
                  ${(result.estimated_value_usd / 1_000_000).toFixed(2)}M
                </div>
                <div className="text-[10px] tracking-[0.3em] uppercase text-[#A3A3A3] mb-8">
                  Confidence: <span className="text-[#F5F5F5]">{result.confidence}</span>
                </div>

                <div className="space-y-6">
                  <div>
                    <div className="text-[10px] tracking-[0.3em] uppercase text-[#D4AF37] mb-2">Market Summary</div>
                    <p className="text-sm text-[#F5F5F5] leading-relaxed font-light">{result.market_summary}</p>
                  </div>
                  <div className="pt-6 border-t border-[#D4AF37]/15">
                    <div className="text-[10px] tracking-[0.3em] uppercase text-[#D4AF37] mb-2">Bridge Analysis</div>
                    <p className="text-sm text-[#F5F5F5] leading-relaxed font-light">{result.bridge_analysis}</p>
                  </div>
                  <div className="pt-6 border-t border-[#D4AF37]/15 grid grid-cols-2 gap-6">
                    <div>
                      <div className="text-[10px] tracking-[0.3em] uppercase text-[#D4AF37] mb-2">Upside</div>
                      <ul className="space-y-2 text-xs text-[#F5F5F5] font-light">
                        {result.upside_factors.map((u, i) => <li key={i} className="flex gap-2"><span className="text-[#D4AF37]">+</span>{u}</li>)}
                      </ul>
                    </div>
                    <div>
                      <div className="text-[10px] tracking-[0.3em] uppercase text-[#A3A3A3] mb-2">Risk</div>
                      <ul className="space-y-2 text-xs text-[#F5F5F5] font-light">
                        {result.risk_factors.map((r, i) => <li key={i} className="flex gap-2"><span className="text-[#A3A3A3]">−</span>{r}</li>)}
                      </ul>
                    </div>
                  </div>
                  <div className="pt-6 border-t border-[#D4AF37]/15">
                    <div className="text-[10px] tracking-[0.3em] uppercase text-[#D4AF37] mb-2">Recommended Posture</div>
                    <p className="text-sm text-[#F5F5F5] leading-relaxed italic font-light">"{result.recommended_action}"</p>
                  </div>
                  <div className="pt-6 border-t border-[#D4AF37]/15">
                    <Link to="/pricing" className="block text-center w-full py-3 border border-[#D4AF37] text-[#D4AF37] text-[10px] tracking-[0.3em] uppercase hover:bg-[#D4AF37] hover:text-[#050505] transition-colors">
                      Upgrade · Shield Pro
                    </Link>
                    <div className="mt-2 text-[9px] tracking-[0.3em] uppercase text-[#A3A3A3] text-center">
                      Unlimited Shield · Appreciation Forecasting
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </aside>
      </div>
    </div>
  );
}
